const FORCE_LOGIN_KEY = 'forceLogin'
const FORCE_LOGIN_COOKIE = 'ps-force-login'
const SKIP_AUTH_CACHE_KEY = 'skipAuthCache'
const SKIP_AUTH_CACHE_COOKIE = 'ps-skip-auth-cache'

function getCookieDomains(hostname: string) {
  const isIpv4Hostname = hostname
    .split('.')
    .every((part) => part.length > 0 && Number.isInteger(Number(part)) && Number(part) >= 0 && Number(part) <= 255)

  if (!hostname || hostname === 'localhost' || isIpv4Hostname) {
    return [undefined]
  }

  const parts = hostname.split('.')
  if (parts.length < 2) {
    return [undefined]
  }

  return [undefined, `.${parts.slice(-2).join('.')}`]
}

function writeCookie(name: string, value: string, expires: string) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  getCookieDomains(window.location.hostname).forEach((domain) => {
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}${secure}`
  })
}

function hasCookie(name: string, value: string) {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .some((cookie) => cookie === `${name}=${value}`)
}

function clearCookie(name: string) {
  writeCookie(name, '', 'Thu, 01 Jan 1970 00:00:00 GMT')
}

export function markForceLogin() {
  localStorage.setItem(FORCE_LOGIN_KEY, 'true')
  localStorage.setItem(SKIP_AUTH_CACHE_KEY, 'true')
  const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
  writeCookie(FORCE_LOGIN_COOKIE, '1', expires)
  writeCookie(SKIP_AUTH_CACHE_COOKIE, '1', expires)
}

// Sets the cache-bypass signal only — without the login-form prompt.
// Used when redirecting a painter from the main app to painter-view so that
// painter-view discards any stale non-painter session it has cached locally.
export function markSkipAuthCache() {
  localStorage.setItem(SKIP_AUTH_CACHE_KEY, 'true')
  const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
  writeCookie(SKIP_AUTH_CACHE_COOKIE, '1', expires)
}

export function consumeForceLogin() {
  // Only honor the cookie — not the localStorage key alone.
  // The localStorage key can be stale (set by a previous logout on this origin and
  // never consumed because the cross-app SSO flow bypassed the login form). The
  // cookie has a 5-minute expiry and is always fresh when present. This means a
  // cross-app redirect from a freshly-authenticated painter will never accidentally
  // show the login form a second time due to a lingering localStorage key.
  const shouldForceLogin = hasCookie(FORCE_LOGIN_COOKIE, '1')

  localStorage.removeItem(FORCE_LOGIN_KEY) // always clean up, even if stale
  clearCookie(FORCE_LOGIN_COOKIE)

  return shouldForceLogin
}

export function shouldSkipAuthCache() {
  return localStorage.getItem(SKIP_AUTH_CACHE_KEY) === 'true' || hasCookie(SKIP_AUTH_CACHE_COOKIE, '1')
}

export function clearSkipAuthCache() {
  localStorage.removeItem(SKIP_AUTH_CACHE_KEY)
  clearCookie(SKIP_AUTH_CACHE_COOKIE)
}

export function getLastLoginHint(): string | undefined {
  const lastLogin = localStorage.getItem('lastLogin')

  if (!lastLogin) {
    return undefined
  }

  try {
    const parsed = JSON.parse(lastLogin)
    return typeof parsed === 'string' ? parsed : undefined
  } catch {
    return lastLogin
  }
}

export function getLoginAuthorizationParams(extraParams: Record<string, string | undefined> = {}) {
  const forceLogin = consumeForceLogin()

  return {
    login_hint: getLastLoginHint(),
    ...extraParams,
    ...(forceLogin ? { prompt: 'login' as const } : {})
  }
}
